/* Workflow runs — the run LEDGER (A-0017 T-50). Every workflow run, from
 * every door (the app at runtime, the Try drawer, the Pilotage MCP, a job),
 * is written by BeXO to the org's own Mongo: full input and output, and every
 * step with its own input/output/status/timing. This page reads it back —
 * filter by workflow, status, door, time range or run id; open a row for the
 * whole record (?run=<id>). Monitoring is BUILDING, not execution, so it
 * lives here, not in the app. */
import React, {
    useCallback,
    useEffect,
    useState
} from 'react';
import {
    useSearchParams
} from 'react-router-dom';
import {
    Activity,
    ArrowLeft,
    RefreshCw,
    Search
} from 'lucide-react';
import {
    appEngineService
} from '../../api/appEngine.service';
import {
    useAppStudio
} from '../../AppStudioContext';
import {
    OutcomeBadge
} from '../../shared/badges';
import JsonTree from '../../shared/JsonTree';
import {
    fmtStamp
} from './RunInspector';
import type {
    RunValueSummary,
    WorkflowRunFilters,
    WorkflowRunRecord,
    WorkflowRunStep
} from '../../api/types';
import {
    isRunValueSummary
} from '../../api/types';

const STATUSES = ['all', 'success', 'failed', 'validation_error', 'running'];
const VIAS: {
    key: string;
    label: string;
}[] = [
    { key: 'all', label: 'every door' },
    { key: 'engine', label: 'app' },
    { key: 'try', label: 'try' },
    { key: 'pilotage', label: 'pilotage' },
    { key: 'job', label: 'job' },
];
const PAGE = 50;

/** The query boxes, in the order they are shown. `key` is the URL param;
 *  the ISO/number conversion happens at apply time. */
const BOXES: {
    key: string;
    label: string;
    placeholder?: string;
    type?: 'text' | 'number' | 'datetime-local';
    mono?: boolean;
}[] = [
    { key: 'name', label: 'Workflow name', placeholder: 'contains…' },
    { key: 'workflow', label: 'Workflow id', placeholder: 'wf_…', mono: true },
    { key: 'id', label: 'Run id', placeholder: 'wr_…', mono: true },
    { key: 'user', label: 'User', placeholder: 'user id', mono: true },
    { key: 'action', label: 'Action', placeholder: 'action', mono: true },
    { key: 'from', label: 'Started from', type: 'datetime-local' },
    { key: 'to', label: 'Started to', type: 'datetime-local' },
    { key: 'min', label: 'Took at least (ms)', placeholder: '0', type: 'number', mono: true },
    { key: 'max', label: 'Took at most (ms)', placeholder: '∞', type: 'number', mono: true },
];
const BOX_KEYS = BOXES.map((b) => b.key);

const SENSITIVE_KEY = /password|secret|token|credential/i;

/** A copy with every sensitive string masked — the tree masks as it draws,
 *  but a one-line preview is one string, so it is masked before it is made. */
function redacted(value: any): any {
    if (Array.isArray(value)) return value.map(redacted);
    if (value && typeof value === 'object') {
        const out: Record<string, any> = {};
        for (const [k, v] of Object.entries(value)) {
            out[k] = typeof v === 'string' && v && SENSITIVE_KEY.test(k) ? '••••' : redacted(v);
        }
        return out;
    }
    return value;
}

function fmtMs(v: number | null | undefined): string {
    if (v == null) return '—';
    if (v < 1000) return `${Math.round(v)} ms`;
    return `${(v / 1000).toFixed(v < 10000 ? 2 : 1)} s`;
}

/** The ledger writes ISO stamps; datetime-local wants a local ISO without
 *  the zone, and gives one back the same way. */
function toLocalInput(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fromLocalInput(v: string): string | null {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d.toISOString();
}

function Fact({
    label,
    children
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div>
            <div className="aps-stat-label">{label}</div>
            <div className="aps-mono" style={{
                fontSize: 'var(--aps-text-sm)',
                marginTop: 2,
                wordBreak: 'break-all'
            }}>
                {children}
            </div>
        </div>
    );
}

function Section({
    title,
    right,
    children
}: {
    title: string;
    right?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <div className="aps-card" style={{
            padding: 16,
            marginTop: 14
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 8
            }}>
                <div className="aps-stat-label">{title}</div>
                {right}
            </div>
            {children}
        </div>
    );
}

/** One value in a key–value table: scalars plain, nested values as a
 *  one-line summary that opens into the tree on click, secrets masked. */
function FieldValue({
    name,
    value
}: {
    name: string;
    value: any;
}) {
    const [open, setOpen] = useState(false);
    const [shown, setShown] = useState(false);
    if (value === null || value === undefined) {
        return <span className="aps-tree-val-null">—</span>;
    }
    if (typeof value === 'object') {
        const isArr = Array.isArray(value);
        const n = isArr ? value.length : Object.keys(value).length;
        const what = isArr ? `list of ${n}` : `${n} field${n === 1 ? '' : 's'}`;
        const line = JSON.stringify(redacted(value));
        return (
            <div>
                <button className="aps-btn aps-btn-ghost aps-btn-sm" onClick={() => setOpen((o) => !o)}
                    title={open ? 'Collapse' : 'Open'}>
                    {open ? '▾' : '▸'} {what}
                </button>
                {!open && (
                    <span className="aps-mono aps-muted" style={{
                        marginLeft: 8,
                        fontSize: 'var(--aps-text-sm)'
                    }}>
                        {line.length > 140 ? line.slice(0, 137) + '…' : line}
                    </span>
                )}
                {open && <JsonTree value={value} />}
            </div>
        );
    }
    if (typeof value === 'string' && value && SENSITIVE_KEY.test(name)) {
        return (
            <button className={'aps-tree-secret' + (shown ? ' revealed' : '')}
                onClick={() => setShown((s) => !s)}
                title={shown ? 'Click to mask again' : 'Sensitive value — click to reveal'}>
                {shown ? value : '••••••••'}
            </button>
        );
    }
    if (typeof value === 'string') {
        return <span className="aps-mono" style={{
            whiteSpace: 'pre-wrap'
        }}>{value}</span>;
    }
    return <span className="aps-mono aps-tree-val-num">{String(value)}</span>;
}

/** A record as a two-column table — one row per field. Lists get one row
 *  per item; a bare scalar is a single "value" row. */
function fmtBytes(n: number): string {
    if (!(n >= 0)) return '?';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** The ledger kept this value's size and shape, not the value (T-64). Say
 *  what it was and why it is not here, instead of tabling the marker. */
function SummaryNote({
    value
}: {
    value: RunValueSummary;
}) {
    const shape = value.type === 'null'
        ? 'nothing'
        : value.items != null
            ? `${value.items} item${value.items === 1 ? '' : 's'}${value.total != null ? ` of ${value.total}` : ''}`
            : value.fields != null
                ? `${value.fields} field${value.fields === 1 ? '' : 's'}`
                : value.type;
    return (
        <div className="aps-muted" style={{
            fontSize: 'var(--aps-text-sm)'
        }}>
            {value.type === 'null' ? (
                <span>nothing</span>
            ) : value._truncated ? (
                <span>
                    <b>Too large to keep</b> — {fmtBytes(value.bytes)}, {shape}. Values over the limit are
                    described, not stored.
                </span>
            ) : (
                <span>
                    <b>Not stored</b> — {fmtBytes(value.bytes)}, {shape}. The log keeps what a successful run
                    produced as size and shape only; the data itself is in the app's collections. A run
                    that fails keeps its data.
                </span>
            )}
        </div>
    );
}

function FieldsTable({
    value
}: {
    value: any;
}) {
    if (value === null || value === undefined) {
        return <div className="aps-muted">nothing</div>;
    }
    if (isRunValueSummary(value)) {
        return <SummaryNote value={value} />;
    }
    const rows: Array<[string, any]> = Array.isArray(value)
        ? value.map((v, i) => [String(i), v] as [string, any])
        : typeof value === 'object'
            ? Object.entries(value)
            : [['value', value]];
    if (rows.length === 0) {
        return <div className="aps-muted">{Array.isArray(value) ? 'empty list' : 'no fields'}</div>;
    }
    return (
        <div className="aps-table-wrap">
            <table className="aps-table">
                <thead>
                    <tr>
                        <th style={{
                            width: '26%'
                        }}>Field</th>
                        <th>Value</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(([k, v]) => (
                        <tr key={k} style={{
                            cursor: 'default'
                        }}>
                            <td className="aps-mono" style={{
                                fontWeight: 600,
                                verticalAlign: 'top',
                                wordBreak: 'break-all'
                            }}>
                                {k}
                            </td>
                            <td style={{
                                wordBreak: 'break-word'
                            }}>
                                <FieldValue name={k} value={v} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** Input or Output: the table by default, the raw tree one click away. */
function DataBlock({
    title,
    value
}: {
    title: string;
    value: any;
}) {
    const [raw, setRaw] = useState(false);
    if (isRunValueSummary(value)) {
        return (
            <Section title={title}>
                <SummaryNote value={value} />
            </Section>
        );
    }
    return (
        <Section title={title} right={
            <div className="aps-chip-row">
                <button className={'aps-filter-chip' + (!raw ? ' active' : '')} onClick={() => setRaw(false)}>table</button>
                <button className={'aps-filter-chip' + (raw ? ' active' : '')} onClick={() => setRaw(true)}>json</button>
            </div>
        }>
            {raw ? <JsonTree value={value ?? null} /> : <FieldsTable value={value} />}
        </Section>
    );
}

function StepRow({
    step
}: {
    step: WorkflowRunStep;
}) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <tr onClick={() => setOpen((o) => !o)}>
                <td className="aps-mono">{step.n}</td>
                <td className="aps-mono" style={{
                    fontWeight: 600
                }}>
                    {step.id}
                </td>
                <td className="aps-mono">{step.kind}{step.calls ? ` → ${step.calls}` : ''}</td>
                <td><OutcomeBadge outcome={{
                    ok: !!step.ok,
                    status: step.status
                }} /></td>
                <td style={{
                    whiteSpace: 'nowrap'
                }}>
                    {fmtStamp(step.started_at)}
                </td>
                <td style={{
                    whiteSpace: 'nowrap'
                }}>
                    {fmtStamp(step.ended_at)}
                </td>
                <td className="aps-mono" style={{
                    whiteSpace: 'nowrap'
                }}>
                    {fmtMs(step.duration_ms)}
                </td>
            </tr>
            {open && (
                <tr style={{
                    cursor: 'default'
                }}>
                    <td colSpan={7} style={{
                        background: 'var(--aps-surface-2, transparent)'
                    }}>
                        {step.error && (
                            <div className="aps-mono" style={{
                                color: 'var(--aps-danger, #b42318)',
                                marginBottom: 8,
                                fontSize: 'var(--aps-text-sm)'
                            }}>
                                {step.error}
                            </div>
                        )}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 12
                        }}>
                            <div>
                                <div className="aps-stat-label" style={{
                                    marginBottom: 6
                                }}>Input</div>
                                <FieldsTable value={step.input} />
                            </div>
                            <div>
                                <div className="aps-stat-label" style={{
                                    marginBottom: 6
                                }}>Output</div>
                                <FieldsTable value={step.output} />
                            </div>
                        </div>
                        {step.decisions && step.decisions.length > 0 && (
                            <div style={{
                                marginTop: 10
                            }}>
                                <div className="aps-stat-label" style={{
                                    marginBottom: 6
                                }}>Decisions</div>
                                <FieldsTable value={step.decisions} />
                            </div>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}

function WorkflowRunDetail({
    runId,
    onBack
}: {
    runId: string;
    onBack: () => void;
}) {
    const {
        tenancy
    } = useAppStudio();
    const [run, setRun] = useState<WorkflowRunRecord | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setRun(await appEngineService.getWorkflowRun(tenancy, runId));
        } catch (e: any) {
            setRun(null);
            setError(e?.response?.data?.detail || e?.message || 'could not load this run');
        } finally {
            setLoading(false);
        }
    }, [tenancy, runId]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <>
            <div className="aps-page-head">
                <div className="aps-page-head-text">
                    <button className="aps-btn aps-btn-ghost aps-btn-sm" onClick={onBack} style={{
                        marginBottom: 8
                    }}>
                        <ArrowLeft size={14} /> All workflow runs
                    </button>
                    <h1 className="aps-h2 aps-mono">{runId}</h1>
                    {run && (
                        <p className="aps-page-sub">
                            {run.workflow_name || run.workflow_id} — {run.step_count ?? run.steps?.length ?? 0} steps
                            recorded, {fmtMs(run.duration_ms)}.
                        </p>
                    )}
                </div>
                <button className="aps-icon-btn" title="Refresh" onClick={load}>
                    <RefreshCw size={15} className={loading ? 'aps-spin' : undefined} />
                </button>
            </div>
            {error && <div className="aps-errors" style={{
                padding: 12
            }}>{error}</div>}
            {run && (
                <>
                    <div className="aps-card" style={{
                        padding: 16,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: 14
                    }}>
                        <Fact label="Status">
                            <OutcomeBadge outcome={{
                                ok: !!run.ok,
                                status: run.status
                            }} />
                        </Fact>
                        <Fact label="Workflow">{run.workflow_id}</Fact>
                        <Fact label="Name">{run.workflow_name || '—'}</Fact>
                        <Fact label="Door">{run.via}</Fact>
                        <Fact label="Started">{fmtStamp(run.started_at)}</Fact>
                        <Fact label="Ended">{fmtStamp(run.ended_at)}</Fact>
                        <Fact label="Duration">{fmtMs(run.duration_ms)}</Fact>
                        <Fact label="Expires">{fmtStamp(run.expires_at)}</Fact>
                        <Fact label="User">{run.user_id || '—'}</Fact>
                        <Fact label="Action">{run.action || '—'}</Fact>
                        <Fact label="Job run">{run.job_run_id || '—'}</Fact>
                        <Fact label="Org">{run.org_id}</Fact>
                        <Fact label="App">{run.app_id}</Fact>
                    </div>
                    {run.error && (
                        <div className="aps-errors" style={{
                            padding: 12,
                            marginTop: 14
                        }}>
                            <div className="aps-errors-title">Error</div>
                            <div className="aps-mono">{run.error}</div>
                        </div>
                    )}
                    <DataBlock title="Input" value={run.input} />
                    <DataBlock title="Output" value={run.output} />
                    <Section title={`Steps (${run.steps?.length ?? 0}) — click a step for its input and output (kept whole only when the step failed)`}>
                        <div className="aps-table-wrap">
                            <table className="aps-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Step</th>
                                        <th>Kind</th>
                                        <th>Status</th>
                                        <th>Started</th>
                                        <th>Ended</th>
                                        <th>Took</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(run.steps || []).map((s) => <StepRow key={`${s.n}-${s.id}`} step={s} />)}
                                    {(run.steps || []).length === 0 && (
                                        <tr style={{
                                            cursor: 'default'
                                        }}>
                                            <td colSpan={7} className="aps-muted">No steps recorded.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Section>
                </>
            )}
        </>
    );
}

export default function WorkflowRunsPage() {
    const {
        tenancy
    } = useAppStudio();
    const [search, setSearch] = useSearchParams();
    const [items, setItems] = useState<WorkflowRunRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const activeRun = search.get('run');
    const status = search.get('status') || 'all';
    const via = search.get('via') || 'all';
    const skip = Number(search.get('skip') || 0);
    // the applied query, one string per box (from/to are ISO in the URL)
    const q: Record<string, string> = {};
    for (const k of BOX_KEYS) q[k] = search.get(k) || '';
    const qKey = BOX_KEYS.map((k) => q[k]).join(' ');

    // Boxes are typed freely and applied on Enter / Search, so every
    // keystroke does not become a query.
    const toDrafts = (src: Record<string, string>) => {
        const d: Record<string, string> = {};
        for (const k of BOX_KEYS) {
            d[k] = k === 'from' || k === 'to' ? toLocalInput(src[k] || null) : src[k];
        }
        return d;
    };
    const [draft, setDraft] = useState<Record<string, string>>(() => toDrafts(q));
    useEffect(() => {
        setDraft(toDrafts(q));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qKey]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const f: WorkflowRunFilters = {
                limit: PAGE,
                skip
            };
            if (status !== 'all') f.status = status;
            if (via !== 'all') f.via = via;
            if (q.name) f.workflow_name = q.name;
            if (q.workflow) f.workflow_id = q.workflow;
            if (q.id) f.run_id = q.id;
            if (q.user) f.user_id = q.user;
            if (q.action) f.action = q.action;
            if (q.from) f.from = q.from;
            if (q.to) f.to = q.to;
            if (q.min) f.min_ms = q.min;
            if (q.max) f.max_ms = q.max;
            const page = await appEngineService.listWorkflowRuns(tenancy, f);
            setItems(page.items);
            setTotal(page.total);
        } catch (e: any) {
            setItems([]);
            setTotal(0);
            setError(e?.response?.data?.detail || e?.message || 'could not load workflow runs');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenancy, status, via, qKey, skip]);

    useEffect(() => {
        if (!activeRun) load();
    }, [load, activeRun]);

    const setParams = (patch: Record<string, string | null>) => {
        const next = new URLSearchParams(search);
        for (const [k, v] of Object.entries(patch)) {
            if (v) next.set(k, v);
            else next.delete(k);
        }
        setSearch(next);
    };

    const apply = () => {
        const patch: Record<string, string | null> = {
            skip: null
        };
        for (const k of BOX_KEYS) {
            const v = (draft[k] || '').trim();
            patch[k] = k === 'from' || k === 'to' ? fromLocalInput(v) : (v || null);
        }
        setParams(patch);
    };

    const clear = () => {
        const patch: Record<string, string | null> = {
            status: null, via: null, skip: null
        };
        for (const k of BOX_KEYS) patch[k] = null;
        setParams(patch);
    };

    const filtered = status !== 'all' || via !== 'all' || BOX_KEYS.some((k) => !!q[k]);

    return (
        <div className="aps-page">
            <div className="aps-page-narrow">
                {activeRun ? (
                    <WorkflowRunDetail runId={activeRun} onBack={() => setParams({
                        run: null
                    })} />
                ) : (
                    <>
                        <div className="aps-page-head">
                            <div className="aps-page-head-text">
                                <h1 className="aps-h2">Workflow runs</h1>
                                <p className="aps-page-sub">
                                    Every workflow run, from every door, kept in this org&apos;s own
                                    MongoDB — full input and output, and every step with its own
                                    input, output, status and timing.
                                </p>
                            </div>
                            <button className="aps-icon-btn" title="Refresh" onClick={load}>
                                <RefreshCw size={15} className={loading ? 'aps-spin' : undefined} />
                            </button>
                        </div>

                        <div className="aps-chip-row" style={{
                            marginBottom: 10
                        }}>
                            {STATUSES.map((s) => (
                                <button key={s}
                                    className={'aps-filter-chip' + (status === s ? ' active' : '')}
                                    onClick={() => setParams({
                                        status: s === 'all' ? null : s,
                                        skip: null
                                    })}>
                                    {s === 'all' ? 'any status' : s}
                                </button>
                            ))}
                            <span style={{
                                width: 10
                            }} />
                            {VIAS.map((v) => (
                                <button key={v.key}
                                    className={'aps-filter-chip' + (via === v.key ? ' active' : '')}
                                    onClick={() => setParams({
                                        via: v.key === 'all' ? null : v.key,
                                        skip: null
                                    })}>
                                    {v.label}
                                </button>
                            ))}
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                            gap: 8,
                            alignItems: 'end',
                            marginBottom: 8
                        }}>
                            {BOXES.map((b) => (
                                <label key={b.key}>
                                    <div className="aps-stat-label">{b.label}</div>
                                    <input className={'aps-input' + (b.mono ? ' aps-input-mono' : '')}
                                        type={b.type || 'text'}
                                        placeholder={b.placeholder}
                                        min={b.type === 'number' ? 0 : undefined}
                                        value={draft[b.key] || ''}
                                        onChange={(e) => setDraft((d) => ({
                                            ...d,
                                            [b.key]: e.target.value
                                        }))}
                                        onKeyDown={(e) => e.key === 'Enter' && apply()} />
                                </label>
                            ))}
                            <div style={{
                                display: 'flex',
                                gap: 6
                            }}>
                                <button className="aps-btn aps-btn-primary" onClick={apply}>
                                    <Search size={14} /> Search
                                </button>
                                {filtered && (
                                    <button className="aps-btn aps-btn-ghost" onClick={clear}>Clear</button>
                                )}
                            </div>
                        </div>
                        <div className="aps-meta" style={{
                            marginBottom: 14
                        }}>
                            Fill any boxes and press Enter or Search. Name matches anywhere in the name; ids, user and action match exactly.
                        </div>

                        {error && <div className="aps-errors" style={{
                            padding: 12,
                            marginBottom: 12
                        }}>{error}</div>}

                        <div className="aps-meta" style={{
                            marginBottom: 8
                        }}>
                            {loading ? 'Loading…' : `${total} run${total === 1 ? '' : 's'}${filtered ? ' match' : ''}`}
                            {total > PAGE && ` — showing ${skip + 1}–${Math.min(skip + PAGE, total)}`}
                        </div>

                        <div className="aps-table-wrap">
                            <table className="aps-table">
                                <thead>
                                    <tr>
                                        <th>Run</th>
                                        <th>Workflow</th>
                                        <th>Status</th>
                                        <th>Door</th>
                                        <th>User</th>
                                        <th>Started</th>
                                        <th>Ended</th>
                                        <th>Took</th>
                                        <th>Steps</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((r) => (
                                        <tr key={r.id} onClick={() => setParams({
                                            run: r.id
                                        })}>
                                            <td className="aps-mono" style={{
                                                fontWeight: 600
                                            }}>
                                                {r.id}
                                            </td>
                                            <td className="aps-mono" title={r.workflow_id}>
                                                {r.workflow_name || r.workflow_id}
                                            </td>
                                            <td><OutcomeBadge outcome={{
                                                ok: !!r.ok,
                                                status: r.status
                                            }} /></td>
                                            <td className="aps-mono">{r.via}</td>
                                            <td className="aps-mono" title={r.user_id || ''} style={{
                                                maxWidth: 120,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {r.user_id || '—'}
                                            </td>
                                            <td style={{
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {fmtStamp(r.started_at)}
                                            </td>
                                            <td style={{
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {fmtStamp(r.ended_at)}
                                            </td>
                                            <td className="aps-mono" style={{
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {fmtMs(r.duration_ms)}
                                            </td>
                                            <td className="aps-mono">{r.step_count ?? '—'}</td>
                                        </tr>
                                    ))}
                                    {!loading && items.length === 0 && (
                                        <tr style={{
                                            cursor: 'default'
                                        }}>
                                            <td colSpan={9}>
                                                <div className="aps-empty" style={{
                                                    padding: '30px 10px'
                                                }}>
                                                    <span className="aps-empty-icon"><Activity size={20} /></span>
                                                    <span>
                                                        {filtered
                                                            ? 'No runs match these filters.'
                                                            : 'No workflow runs yet — run a workflow from the app, the Try drawer or Pilotage and it lands here.'}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {total > PAGE && (
                            <div style={{
                                display: 'flex',
                                gap: 6,
                                justifyContent: 'flex-end',
                                marginTop: 10
                            }}>
                                <button className="aps-btn aps-btn-ghost aps-btn-sm" disabled={skip <= 0}
                                    onClick={() => setParams({
                                        skip: skip - PAGE > 0 ? String(skip - PAGE) : null
                                    })}>
                                    Newer
                                </button>
                                <button className="aps-btn aps-btn-ghost aps-btn-sm" disabled={skip + PAGE >= total}
                                    onClick={() => setParams({
                                        skip: String(skip + PAGE)
                                    })}>
                                    Older
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
